<template>
<article>
    <p>
        Every developer understands that in order to optimize something, you must first measure it.
        The same principle applies to you cloud costs - measuring them is crucial for optimization.
    </p>

    <p>
        Now you can easily monitor you cloud costs with Coroot.
    </p>

    <v-card>
        <v-img eager src="/static/img/docs/coroot-ce/cloud_cost/overview.png" />
    </v-card>

    <p>
        Don't underestimate the power of simple optimizations - they can be highly effective.
        When it comes to cloud costs, removing unused resources is often more effective than optimizing your code.
    </p>


    <h2 id="nodes">Nodes</h2>

    <p>
        By breaking down costs by nodes, you can easily identify the least utilized nodes (by sorting the table according to Idle costs),
        understand the usage of each node based on actual CPU/memory usage and requests, and determine which resources are overprovisioned (requested but not used).
    </p>

    <v-card>
        <v-img eager src="/static/img/docs/coroot-ce/cloud_cost/nodes.png" />
    </v-card>

    <h2 id="applications">Applications</h2>

    <p>
        Coroot simplifies the monitoring of large infrastructures by introducing the concept of application categories.
        By default, Coroot automatically categorizes apps related to <b>control-plane</b> and <b>monitoring</b>,
        but you can customize your categories easily by defining masks for application names and namespaces.
    </p>

    <p>
        Coroot breaks down costs by categories, but you can easily drill down to any category and view cost allocations by application.
    </p>

    <ul>
        <li>
            <b>Usage Costs</b>: actual CPU/Memory usage of the application multiplied by the resource cost on a specific node.
        </li>
        <li>
            <b>Allocation Costs</b>: requested resources (CPU/Memory request in Kubernetes) by the application multiplied by the resource cost on a specific node.
        </li>
        <li>
            <b>Overprovisioning Costs</b>: difference between Allocation Costs and Usage Costs for the application.
            This indicates that the app requested more resources than its actual usage.
        </li>
    </ul>

    <v-card>
        <v-img eager src="/static/img/docs/coroot-ce/cloud_cost/apps.gif" />
    </v-card>

    <h2 id="deployment-tracking">Deployment tracking</h2>

    <p>
        Cost monitoring is also integrated with Deployment Tracking.
        If a service starts consuming significantly more CPU time than the previous version, you will immediately see an estimated change in costs.
    </p>

    <v-card>
        <v-img max-width="600px" eager src="/static/img/docs/coroot-ce/cloud_cost/slack.png" />
    </v-card>

    <h2 id="how-it-works">How it works</h2>

    <p>
        Coroot's node-agent <a href="https://coroot.com/blog/cloud-metadata" target="_blank">gathers</a>
        cloud instance metadata of every node and exports it as the <MetricLink name="node_cloud_info" exporter="node-agent"/> metric.
        With this metric, Coroot can determine the cloud provider, region, type, and purchase options (on-demand/spot/reserved) for each instance.
        By leveraging this information, it can quickly determine the price of each node.
    </p>

    <p>
        To calculate the cost of each resource separately, Coroot assumes that 1 CPU core costs the same as 1GB of memory.
        By doing so, the CPU and memory usage of every application can be easily translated into $$$.
    </p>

    <h2 id="limitations">Limitations</h2>

    <p>
        Coroot has some limitations that are important to note.
    </p>

    <ul>
        <li>Only AWS, GCP, and Azure are currently supported</li>
        <li>Standard pricing (without discounts)</li>
        <li>The cost calculation considers only CPU and Memory usage (support for GPUs, volumes, and traffic will be added later)</li>
        <li>Currently, the cost calculation considers only compute, AWS RDS, and AWS ElastiCache instances (support for EKS/AKS/GKE will be added later)</li>
        <li>Reserved instances are not supported yet</li>
    </ul>



</article>
</template>

<script>
import {defineComponent} from "vue";
import MetricLink from "@/components/MetricLink.vue";

export default defineComponent({
    components: {MetricLink}
})
</script>

<style scoped>

</style>
